import { EnquiryForm, EnquiryFormValues } from './types';

/**
 * The shape of the GA4 event for a submitted offer enquiry - MP-04, "Make an Enquiry".
 *
 * Split from the code that fires it so it can be unit tested: this file imports nothing aliased, and jest in
 * this repository has no mapper for the ~/ paths, so a module that reaches for one cannot be tested at all.
 * The event's keys are a contract with analytics, which makes them worth pinning.
 */

export type EnquiryVehicle = {
  make?: string;
  model?: string;
};

export type EnquiryUserData = {
  emailHashed?: string;
  phoneHashed?: string;
  postcode?: string;
};

/** The value of the first field of a given kind, trimmed, or undefined when the form has none. */
export function valueOfKind(
  form: EnquiryForm,
  values: EnquiryFormValues,
  kind: string
): string | undefined {
  const field = form.fields.find((candidate) => candidate.fieldKind === kind);
  if (!field) return undefined;

  const raw = values[field.fieldName];
  if (typeof raw !== 'string') return undefined;

  const value = raw.trim();
  return value.length > 0 ? value : undefined;
}

/**
 * The event object, built from values that have already been hashed.
 *
 * Kept separate from the hashing so the shape can be tested without a crypto implementation, and so the one
 * thing analytics actually checks - the exact keys - is pinned by a test.
 */
export function buildEnquirySubmittedEvent(
  user: EnquiryUserData,
  vehicle?: EnquiryVehicle,
  externalLeadId?: string
): Record<string, unknown> {
  const event: Record<string, unknown> = {
    event: '_formNavigate',
    form: {
      name: 'offer enquiry',
      stage: 'submitted',
    },
    user: withoutEmpty(user),
  };

  const details: Record<string, unknown> = {};

  // The key the lead was published under. The older enquiry page reports exactly this, under exactly this
  // name, which is how CDP ties a reported conversion to the lead Salesforce received - so it is reported
  // here the same way rather than in a shape of this page's own invention.
  if (externalLeadId) {
    details.externalLeadId = externalLeadId;
  }

  // Only when the offer is about a vehicle, which is decided by the model and not by the make: every offer on
  // this site is a Subaru, so a make on its own says nothing. A servicing or accessory offer has no model, and
  // sending {make} alone would put a row with a blank model in the vehicle breakdown rather than leaving the
  // offer out of it - reporting an accessory enquiry as a vehicle enquiry with the car missing.
  if (vehicle?.model) {
    details.vehicleSelected = [withoutEmpty(vehicle)];
  }

  // Absent rather than empty: an empty details object reads as "we looked and found nothing", and the older
  // page sends no details at all when it has none.
  if (Object.keys(details).length > 0) {
    (event.form as Record<string, unknown>).details = details;
  }

  return event;
}

/** Drops the keys with nothing in them, so the event carries no empty strings or undefined values. */
export function withoutEmpty(
  input: Record<string, unknown>
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(input).filter(
      ([, value]) => typeof value === 'string' && value.length > 0
    )
  ) as Record<string, string>;
}
